import React from "react";
import { useIntl } from "react-intl";
import { Tooltip } from "@eventbrite/marmalade";
import {
  PremiumOrganizerBadgeIcon,
  PREMIUM_ORGANIZER_BADGE_COLOR,
  ULTIMATE_ORGANIZER_BADGE_COLOR,
} from "@/src/listings/components/_shared/Icons";
import styles from "./OrganizerNameWithBadge.module.scss";

interface OrganizerNameWithBadgeProps {
  name: string;
  isPremiumOrganizer: boolean;
  isUltimateOrganizer?: boolean;
}

// Renders the organizer name with the subscription badge glued to the last word so it moves with the name.
export const OrganizerNameWithBadge: React.FC<OrganizerNameWithBadgeProps> = ({
  name,
  isPremiumOrganizer,
  isUltimateOrganizer = false,
}) => {
  const { formatMessage } = useIntl();
  const [isBadgeTooltipOpen, setIsBadgeTooltipOpen] = React.useState(false);

  if (!isPremiumOrganizer && !isUltimateOrganizer) {
    return <>{name}</>;
  }

  const nameWords = name.trim().split(/\s+/);
  const nameLastWord = nameWords[nameWords.length - 1];
  const namePrefix = nameWords.slice(0, -1).join(" ");

  return (
    <>
      {namePrefix && `${namePrefix} `}
      <span className={styles.premiumNameEnd}>
        {nameLastWord}
        <Tooltip
          content={formatMessage(
            isUltimateOrganizer
              ? {
                  id: "This organizer has an Eventbrite Ultimate subscription.",
                  defaultMessage:
                    "This organizer has an Eventbrite Ultimate subscription.",
                  description: "Ultimate organizer badge tooltip",
                }
              : {
                  id: "This organizer has an Eventbrite Premium subscription.",
                  defaultMessage:
                    "This organizer has an Eventbrite Premium subscription.",
                  description: "Premium organizer badge tooltip",
                },
          )}
          className={styles.premiumTooltip}
          providerProps={{ delayDuration: 0 }}
          rootProps={{
            open: isBadgeTooltipOpen,
            onOpenChange: setIsBadgeTooltipOpen,
          }}
          contentProps={{ side: "top", sideOffset: 6, avoidCollisions: false }}
        >
          <span
            className={styles.premiumBadge}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsBadgeTooltipOpen((open) => !open);
            }}
            role="img"
            aria-label={formatMessage(
              isUltimateOrganizer
                ? {
                    id: "Ultimate organizer",
                    defaultMessage: "Ultimate organizer",
                    description:
                      "Accessible label for the ultimate organizer badge",
                  }
                : {
                    id: "Premium organizer",
                    defaultMessage: "Premium organizer",
                    description:
                      "Accessible label for the premium organizer badge",
                  },
            )}
          >
            <PremiumOrganizerBadgeIcon
              color={
                isUltimateOrganizer
                  ? ULTIMATE_ORGANIZER_BADGE_COLOR
                  : PREMIUM_ORGANIZER_BADGE_COLOR
              }
            />
          </span>
        </Tooltip>
      </span>
    </>
  );
};
